
import { Component, ChangeDetectionStrategy, inject, signal, OnInit, input, effect, output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common'; 
import { Title } from '@angular/platform-browser'; 

import { DataService } from '../../services/data.service';
import { Prescription, ClinicSettings } from '../../models/index';
import { NotificationService } from '../../services/notification.service';

declare var html2canvas: any;

@Component({
  selector: 'app-medical-certificate',
  standalone: true,
  imports: [CommonModule], 
  templateUrl: './medical-certificate.component.html',
  styleUrls: ['./medical-certificate.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MedicalCertificateComponent implements OnInit {
  private route: ActivatedRoute = inject(ActivatedRoute);
  private dataService = inject(DataService);
  private titleService: Title = inject(Title);
  private notificationService = inject(NotificationService);
  
  prescriptionIdInput = input<string | null>(null, { alias: 'prescriptionId' });
  
  // Output event for closing the component when embedded
  close = output<void>();
  
  // Data Signals
  certData = signal<any | null>(null);
  clinicSettings = signal<ClinicSettings | null>(null);
  signatureUrl = signal<string | null>(null);
  
  isLoading = signal(true);
  isGeneratingImage = signal(false);
  
  // Date displayed on the certificate
  certificateDate = new Date();

  constructor() {
    effect(() => {
        const id = this.prescriptionIdInput();
        if (id) {
            this.loadData(id);
        }
    });
  }

  ngOnInit(): void {
    const routeId = this.route.snapshot.paramMap.get('prescriptionId');
    if (routeId) {
      this.loadData(routeId);
    } else if (!this.prescriptionIdInput()) {
      this.isLoading.set(false);
    }
  }
  
  onClose() {
      this.close.emit();
  }

  async loadData(id: string): Promise<void> {
    this.isLoading.set(true);
    try {
      // Reuse the secure prescription fetcher which gets Patient + Doctor + Clinic info
      const rawData = await this.dataService.getPrescriptionForPrinting(id);
      
      if (!rawData) {
          throw new Error('Certificate data not found');
      }

      this.certData.set(rawData);

      // --- DATE FIX: Strictly use reviewedAt (Approval Date) ---
      const validDateStr = rawData.reviewedAt || rawData.createdAt;
      if (validDateStr) {
          this.certificateDate = new Date(validDateStr);
      }
      // --------------------------------------------------------

      // Extract Clinic Settings (Provider Details)
      let clinicData: ClinicSettings | null = null;
      if (rawData.providerDetails) {
          const pd = rawData.providerDetails;
          clinicData = {
              licenseNumber: pd.license_number || '',
              professionType: pd.profession_type || 'MODERN',
              addressText: pd.address || '',
              headerText: pd.header_text || 'ใบรับรองแพทย์',
              certificateLogoUrl: pd.logo_url,
              certificateBgUrl: pd.bg_url,
              doctorSignatureUrl: pd.signature_url,
              watermarkText: pd.watermark_text || '',
              operatingHours: '', 
              enableTelemedAutoApprove: false 
          };
      }
      this.clinicSettings.set(clinicData);

      // Determine Signature (Snapshot > Clinic Central)
      const finalSig = rawData.doctor_signature_snapshot || clinicData?.doctorSignatureUrl || null;
      this.signatureUrl.set(finalSig);

      // Set Page Title for cleaner PDF filenames
      const patientName = rawData.customer?.fullName || 'Patient';
      const dateStr = this.certificateDate.toISOString().split('T')[0];
      this.titleService.setTitle(`Medical_Cert_${patientName}_${dateStr}`);

      // If loaded directly via URL (not embedded), trigger print automatically
      // We check if we are NOT embedded (no input ID provided via binding)
      if (!this.prescriptionIdInput()) {
         setTimeout(() => window.print(), 800);
      }

    } catch (e: any) {
      console.error("Failed to load certificate data:", e);
      this.notificationService.showError("ไม่สามารถโหลดข้อมูลใบรับรองแพทย์ได้");
    } finally {
      this.isLoading.set(false);
    }
  }

  get doctorName(): string {
      return this.certData()?.doctorName || '........................................................';
  }

  get patientName(): string {
      return this.certData()?.customer?.fullName || '........................................................';
  }

  get patientAge(): string {
      const dob = this.certData()?.customer?.birthDate;
      if (!dob) return '...';
      const birthDate = new Date(dob);
      const today = new Date();
      const ageDifMs = today.getTime() - birthDate.getTime();
      const ageDate = new Date(ageDifMs);
      return Math.abs(ageDate.getUTCFullYear() - 1970).toString();
  }

  get diagnosis(): string {
      // Use approvedItems instead of approved_items
      return this.certData()?.approvedItems?.symptoms || '-';
  }

  get thaiDate(): string {
      const d = this.certificateDate; // Use the fixed certificate date
      const months = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
      return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear() + 543}`;
  }

  // --- New Getters for Smart Format ---
  
  get prescriptionText(): string {
      // Use approvedItems instead of approved_items
      const items = this.certData()?.approvedItems;
      if (!items) return 'ช่อดอกกัญชาแห้ง (Dried Cannabis Flower)';
      
      // Dynamic text based on unit to be safe, though prompt requested 'Flower' focus
      if (items.dosageUnit === 'PIECE') {
          return 'ผลิตภัณฑ์กัญชา (Cannabis Product)';
      } else if (items.dosageUnit === 'ML') {
          return 'น้ำมันกัญชา (Cannabis Oil)';
      }
      return 'ช่อดอกกัญชาแห้ง (Dried Cannabis Flower)';
  }

  get totalAmount(): string {
      // Use approvedItems instead of approved_items
      const amount = this.certData()?.approvedItems?.totalAmount;
      return amount ? amount.toLocaleString() : '...';
  }
  
  get amountUnit(): string {
      // Use approvedItems instead of approved_items
      const unit = this.certData()?.approvedItems?.dosageUnit;
      if (unit === 'PIECE') return 'ชิ้น/ขวด';
      if (unit === 'ML') return 'มิลลิลิตร';
      return 'กรัม';
  }

  get durationDays(): string {
      // Use approvedItems instead of approved_items
      const days = this.certData()?.approvedItems?.durationDays;
      return days ? days.toString() : '...';
  }

  get qrCodeUrl(): string {
      const id = this.certData()?.id;
      if (!id) return '';
      
      // Construct the verification URL based on the current origin
      // This ensures it works on both localhost and production
      const origin = window.location.origin; // e.g., https://chuamkan.com
      const verifyUrl = `${origin}/#/verify/${id}`;
      
      // Encode the URL to be safe for QR API
      const encodedUrl = encodeURIComponent(verifyUrl);
      
      return `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodedUrl}`;
  }

  async downloadImage() {
    if (typeof html2canvas === 'undefined') {
      this.notificationService.showWarning('Image library missing.');
      return;
    }
    const element = document.querySelector('.A4-cert-paper') as HTMLElement;
    if (!element) return;
    
    this.isGeneratingImage.set(true);
    try {
      const canvas = await html2canvas(element, { scale: 3, useCORS: true, allowTaint: true, backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      const filename = `MedicalCert_${this.patientName.replace(/\s/g, '_')}.png`;
      link.download = filename;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (err) {
      console.error(err);
      this.notificationService.showError("บันทึกรูปภาพล้มเหลว");
    } finally {
      this.isGeneratingImage.set(false);
    }
  }
}
